import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  Button,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  Alert,
  alpha,
  useTheme,
  Avatar,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  ContentCopy,
  Link as LinkIcon,
  Schedule,
  Cancel,
  Cancel as CancelIcon,
  Search,
  FilterList,
  EventRepeat,
  Repeat,
  Payment,
} from '@mui/icons-material';
import { apiService } from '../services/api.service';
import { firebaseService } from '../services/firebase.service';
import { Booking } from '../types';
import Layout from '../components/Layout';

const Bookings: React.FC = () => {
  const theme = useTheme();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [recurringFilter, setRecurringFilter] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {
        page: page + 1,
        limit: rowsPerPage,
        search,
      };
      if (statusFilter) filters.status = statusFilter;
      if (recurringFilter) filters.recurringOnly = true;

      const result = await apiService.getBookings(filters);
      setBookings(result.data);
      setTotal(result.pagination.total);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, recurringFilter, search]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, booking: Booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    setDetailsOpen(true);
    handleMenuClose();
  };

  const handleCancel = async () => {
    if (selectedBooking) {
      try {
        await apiService.cancelBooking(selectedBooking.id, 'Cancelled by admin');
        loadBookings();
      } catch (error) {
        console.error('Failed to cancel booking:', error);
      }
    }
    handleMenuClose();
  };

  const copyToClipboard = (text: string, label: string = 'Copied!') => {
    navigator.clipboard.writeText(text);
    setSnackbarMessage(`${label} copied to clipboard`);
    setSnackbarOpen(true);
  };

  const getMeetingLink = (booking: Booking): string | null => {
    if (booking.jitsiRoomName) {
      return `https://tutorapp.duckdns.org/${booking.jitsiRoomName}`;
    }
    return null;
  };

  const getPaymentId = (booking: Booking): string | null => {
    return booking.razorpayPaymentId || booking.walletPaymentId || null;
  };

  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'completed': return { color: 'success.main', bg: alpha(theme.palette.success.main, 0.15), border: alpha(theme.palette.success.main, 0.2) };
      case 'accepted': return { color: 'info.main', bg: alpha(theme.palette.info.main, 0.15), border: alpha(theme.palette.info.main, 0.2) };
      case 'pending': return { color: 'warning.main', bg: alpha(theme.palette.warning.main, 0.15), border: alpha(theme.palette.warning.main, 0.2) };
      case 'cancelled':
      case 'rejected': return { color: 'error.main', bg: alpha(theme.palette.error.main, 0.15), border: alpha(theme.palette.error.main, 0.2) };
      default: return { color: 'text.secondary', bg: alpha(theme.palette.grey[500], 0.15), border: alpha(theme.palette.grey[500], 0.2) };
    }
  };

  const calculatePaymentBreakdown = (totalAmount: number) => {
    const classCharges = totalAmount / 1.13;
    const platformFee = classCharges * 0.10;
    const transactionFee = classCharges * 0.03;
    return {
      classCharges: parseFloat(classCharges.toFixed(2)),
      platformFee: parseFloat(platformFee.toFixed(2)),
      transactionFee: parseFloat(transactionFee.toFixed(2)),
      total: totalAmount,
    };
  };


  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return firebaseService.convertTimestamp(dateString).toLocaleDateString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch { return dateString; }
  };


  return (
    <Layout>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: -1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              Booking Records
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Review and manage session bookings and payment status.
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
          <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider', bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.02) }}>
            <TextField
              sx={{
                flex: { xs: '1 1 100%', sm: 1 },
                minWidth: { xs: '100%', sm: 300 },
                '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: theme.palette.mode === 'light' ? 'background.paper' : alpha(theme.palette.common.white, 0.05) }
              }}
              placeholder="Search records..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} /> }}
              size="small"
            />

            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: 'auto' } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                label="Status"
                sx={{ borderRadius: 3, bgcolor: theme.palette.mode === 'light' ? 'background.paper' : alpha(theme.palette.common.white, 0.05) }}
                IconComponent={FilterList}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Chip
                label="Recurring"
                onClick={() => { setRecurringFilter(!recurringFilter); setPage(0); }}
                color={recurringFilter ? 'primary' : 'default'}
                variant={recurringFilter ? 'filled' : 'outlined'}
                icon={<Repeat sx={{ fontSize: '1rem !important' }} />}
                sx={{ borderRadius: 2, fontWeight: 700, p: 0.5, flex: { xs: 1, sm: 'auto' } }}
              />
            </Box>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress size={32} thickness={5} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.04) }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Booking info</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Participants</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Schedule</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Payment Reference</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Status</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookings.map((booking) => {
                      const statusStyle = getStatusStyles(booking.status);
                      return (
                        <TableRow key={booking.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              #{booking.bookingId || booking.id.substring(0, 8)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              <Chip label={booking.subject} size="small" sx={{ fontSize: '0.625rem', height: 18, fontWeight: 700 }} />
                              {booking.isTrialClass && <Chip label="Trial" size="small" color="info" sx={{ fontSize: '0.625rem', height: 18, fontWeight: 700 }} />}
                              {booking.parentBookingId && <Chip label="Recurring" size="small" color="secondary" sx={{ fontSize: '0.625rem', height: 18, fontWeight: 700 }} />}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 18, height: 18, fontSize: '0.6rem', bgcolor: 'primary.light' }}>S</Avatar>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{booking.studentName}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 18, height: 18, fontSize: '0.6rem', bgcolor: 'secondary.light' }}>T</Avatar>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{booking.tutorName}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(booking.date)}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Schedule sx={{ fontSize: 12 }} /> {booking.time}
                            </Typography>
                            {booking.rescheduledDate && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25, fontWeight: 600 }}>
                                → Rescheduled
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>₹{booking.amount}</Typography>
                            <Typography variant="caption" color={booking.paymentStatus === 'paid' ? 'success.main' : 'warning.main'} sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                              {booking.paymentStatus}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {getPaymentId(booking) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 500, fontFamily: 'monospace', bgcolor: alpha(theme.palette.divider, 0.5), px: 0.5, borderRadius: 0.5 }}>
                                  {getPaymentId(booking)?.substring(0, 10)}...
                                </Typography>
                                <IconButton size="small" onClick={() => copyToClipboard(getPaymentId(booking)!, 'Payment ID')} sx={{ p: 0 }}>
                                  <ContentCopy sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={booking.status}
                              size="small"
                              sx={{
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                fontSize: '0.625rem',
                                bgcolor: statusStyle.bg,
                                color: statusStyle.color,
                                border: '1px solid',
                                borderColor: statusStyle.border,
                                borderRadius: 1.5
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton onClick={(e) => handleMenuClick(e, booking)} size="small">
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{ borderTop: 1, borderColor: 'divider' }}
              />
            </>
          )}
        </Paper>

        <Menu
          anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
          elevation={4}
          PaperProps={{ sx: { minWidth: 180, borderRadius: 3, mt: 1 } }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleViewDetails} sx={{ gap: 1.5, py: 1.25 }}>
            <Visibility fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={600}>View Record</Typography>
          </MenuItem>
          {selectedBooking?.status !== 'cancelled' && selectedBooking?.status !== 'completed' && (
            <MenuItem onClick={handleCancel} sx={{ gap: 1.5, py: 1.25, color: 'error.main' }}>
              <Cancel fontSize="small" />
              <Typography variant="body2" fontWeight={600}>Cancel Booking</Typography>
            </MenuItem>
          )}
        </Menu>

        <Dialog
          open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: 4, backgroundImage: 'none' } }}
        >
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>Booking Receipt</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>#{selectedBooking?.bookingId || selectedBooking?.id.substring(0, 12)}</Typography>
              </Box>
              <Chip
                label={selectedBooking?.status}
                color={selectedBooking ? getStatusStyles(selectedBooking.status).color as any : 'default'}
                sx={{ fontWeight: 800, textTransform: 'uppercase', borderRadius: 1.5 }}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {selectedBooking && (
              <Box sx={{ display: 'grid', gap: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.65rem' }}>Student</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedBooking.studentName}</Typography>
                    <Typography variant="caption" color="text.secondary">ID: {selectedBooking.studentId.substring(0, 12)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.65rem' }}>Tutor</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedBooking.tutorName}</Typography>
                    <Typography variant="caption" color="text.secondary">ID: {selectedBooking.tutorId.substring(0, 12)}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.65rem' }}>Subject & Duration</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedBooking.subject}</Typography>
                    <Typography variant="caption" color="text.secondary">{selectedBooking.duration} Minutes</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.65rem' }}>Scheduled For</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(selectedBooking.date)}</Typography>
                    <Typography variant="caption" color="text.secondary">At {selectedBooking.time}</Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 800, textTransform: 'uppercase', mb: 1.5, display: 'block' }}>Payment Breakdown</Typography>
                  {(() => {
                    const b = calculatePaymentBreakdown(selectedBooking.amount);
                    return (
                      <Box sx={{ display: 'grid', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>Class Charges</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>₹{b.classCharges}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Platform Fee (10%)</Typography>
                          <Typography variant="caption" color="text.secondary">₹{b.platformFee}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Transaction Fee (3%)</Typography>
                          <Typography variant="caption" color="text.secondary">₹{b.transactionFee}</Typography>
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>Total Paid</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main' }}>₹{b.total}</Typography>
                        </Box>
                        {selectedBooking.paymentStatus === 'paid' && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1.5, fontSize: '0.65rem', border: '1px dashed', borderColor: 'divider' }}>
                            <strong>Refund Rule:</strong> Class charges (₹{b.classCharges}) are refundable. Fees are non-refundable.
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                </Box>

                {selectedBooking.rescheduledDate && (
                  <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'warning.light', bgcolor: alpha(theme.palette.warning.main, 0.03) }}>
                    <Typography variant="caption" color="warning.main" sx={{ fontWeight: 800, textTransform: 'uppercase', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EventRepeat sx={{ fontSize: 14 }} /> Reschedule Event
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                      From {formatDate(selectedBooking.originalDate || selectedBooking.date)} at {selectedBooking.originalTime || selectedBooking.time}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'warning.main', display: 'block' }}>
                      To {formatDate(selectedBooking.rescheduledDate)} at {selectedBooking.rescheduledTime}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Reason: {selectedBooking.rescheduleReason || 'Not stated'}</Typography>
                  </Box>
                )}

                {selectedBooking.status === 'cancelled' && (
                  <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'error.light', bgcolor: alpha(theme.palette.error.main, 0.03) }}>
                    <Typography variant="caption" color="error.main" sx={{ fontWeight: 800, textTransform: 'uppercase', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CancelIcon sx={{ fontSize: 14 }} /> Cancellation Info
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>By: {selectedBooking.cancelledBy || 'System'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Reason: {selectedBooking.cancellationReason || 'No reason provided'}</Typography>
                    {(selectedBooking.refundAmount || selectedBooking.cancellationCharge) && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                        {selectedBooking.refundAmount && <Typography variant="caption" color="success.main" sx={{ fontWeight: 800 }}>Refund: ₹{selectedBooking.refundAmount}</Typography>}
                        {selectedBooking.cancellationCharge && <Typography variant="caption" color="error.main" sx={{ fontWeight: 800 }}>Charge: ₹{selectedBooking.cancellationCharge}</Typography>}
                      </Box>
                    )}
                  </Box>
                )}

                {getMeetingLink(selectedBooking) && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<LinkIcon />}
                    href={getMeetingLink(selectedBooking)!}
                    target="_blank"
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: 'divider' }}
                  >
                    Join Jitsi Meeting
                  </Button>
                )}
              </Box>
            )}

            <Button onClick={() => setDetailsOpen(false)} variant="contained" fullWidth sx={{ mt: 4, py: 1.5, borderRadius: 3, fontWeight: 700 }}>
              Close Record
            </Button>
          </Box>
        </Dialog>

        <Snackbar
          open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} icon={<Payment />} severity="success" sx={{ width: '100%', borderRadius: 3, fontWeight: 600 }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default Bookings;
